import { useEffect } from 'react';
import { logger } from 'services';
import { taskCollection } from './task-collection';
import { apiTaskToStoreTask } from './to-store';
/**
 * Drives AgGrid row updates from the task collection. When a task changes in
 * the collection (optimistic mutation, WS refetch, or a push from anywhere),
 * the matching visible grid row is updated with the fresh store-shaped value.
 *
 * This makes the server-side grid reactive to the collection — the single
 * source of truth — instead of needing bespoke per-source node updates.
 */ export const useTaskGridCollectionSync = (gridApi, fieldLookup)=>{
    useEffect(()=>{
        if (!gridApi) {
            return undefined;
        }
        return taskCollection.subscribeToChanges((changedIds)=>{
            if (!gridApi || gridApi.isDestroyed()) {
                return;
            }
            const refreshed = [];
            gridApi.forEachNode((node)=>{
                var _node_data;
                const id = (_node_data = node.data) === null || _node_data === void 0 ? void 0 : _node_data.taskId;
                if (id == null || !changedIds.has(id)) {
                    return;
                }
                const task = taskCollection.get(id);
                if (task) {
                    node.setData(apiTaskToStoreTask(task, fieldLookup));
                    refreshed.push(node);
                }
            });
            if (refreshed.length === 0) {
                return;
            }
            // `setData` alone updates the node but does not reliably repaint its
            // cells on the server-side row model: the rendered cell keeps whatever
            // the row was fetched with, so a task that moved phase in another tab
            // stayed in its old phase on screen while the collection underneath was
            // already correct. The socket path has always paired the two — this is
            // the same pairing, for the collection-driven path.
            //
            // Guarded because this runs inside the collection's notification
            // cascade: a throw here does not just skip the repaint, it aborts every
            // subscriber queued behind this one.
            try {
                var _gridApi_refreshCells;
                (_gridApi_refreshCells = gridApi.refreshCells) === null || _gridApi_refreshCells === void 0 ? void 0 : _gridApi_refreshCells.call(gridApi, {
                    rowNodes: refreshed,
                    force: true
                });
            } catch (error) {
                logger.error('task grid refreshCells failed', {
                    error
                });
            }
        });
    }, [
        gridApi,
        fieldLookup
    ]);
};
